<template>
  <div>
    <RevForm
      ref="form"
      :initial-values
      method="POST"
      :validate="validators"
      @submit="handleSubmit"
    >
      <template #default="{ values, errors, hasError }">
        <Addresses
          :billing-address="addressStore.billing"
          :collection-point="addressStore.collectionPoint"
          :items
          :shipping-address="addressStore.shipping"
          @edit-billing="navigateTo({ name: CHECKOUT.BILLING_ADDRESS })"
          @edit-shipping="navigateTo({ name: CHECKOUT.SHIPPING_ADDRESS })"
        />

        <div v-if="userInformationStore.isFormRequired" class="mt-24 md:mt-56">
          <h2 class="heading-3">
            {{ i18n(translations.userInformationTitle) }}
          </h2>
          <RevCard class="mt-16 p-24">
            <UserInformationFields
              v-model:birthdate="values.birthdate"
              v-model:national-id="values.nationalId"
              :country="addressStore.billing.country"
              :errors
              :with-national-id="
                withNationalId || userInformationWithNationalId
              "
            />
          </RevCard>
        </div>

        <Delivery
          :items
          :last-collection-point
          :show-error="hasCollectionPointError"
          @updated="onUpdatedDelivery"
          @updating="onUpdatingDelivery"
        />

        <div class="mt-56 flex justify-end">
          <RevButton
            ref="submit"
            data-qa="submit-button"
            :disabled="isSubmitButtonDisabled(hasError)"
            full-width="adaptive"
            :loading="loading || isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submitButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>

    <ReassuranceItems
      class="mt-56"
      :selected-mobile-plan="getSelectedMobilePlan(items)"
    />
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { BuybackOffer, Country } from '@backmarket/http-api'
import type { Bill } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useBuybackOffer'
import { navigateToPaymentPage } from '@backmarket/nuxt-layer-payment/pages/navigateToPaymentPage'
import { InputAddressBirthdateValidators } from '@backmarket/nuxt-module-address/InputAddressBirthdateValidators'
import { InputAddressNationalIdValidators } from '@backmarket/nuxt-module-address/InputAddressNationalIdValidators'
import UserInformationFields from '@backmarket/nuxt-module-address/UserInformationFields.vue'
import type { NationalIdCountries } from '@backmarket/nuxt-module-address/nationalId'
import { isNationalIdRequiredForCountry } from '@backmarket/nuxt-module-address/utils/nationalId/isNationalIdRequiredForCountry'
import { isNationalIdShownForBilling } from '@backmarket/nuxt-module-address/utils/nationalId/isNationalIdShownForBilling'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevForm, makeValidate } from '@ds/components/Form'

import Delivery from '~/scopes/checkout/components/Delivery/Delivery.vue'
import ReassuranceItems from '~/scopes/checkout/components/ReassuranceItems/ReassuranceItems.vue'
import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import { useSaveBouyguesAddress } from '~/scopes/checkout/composables/useSaveBouyguesAddress'
import { CHECKOUT_LOG_TYPES } from '~/scopes/checkout/config/constants'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { useAddressStore } from '~/scopes/checkout/stores/addressStore'
import { useSwapStore } from '~/scopes/checkout/stores/swapStore'
import { useUserInformationStore } from '~/scopes/checkout/stores/userInformationStore'
import { getSelectedMobilePlan } from '~/scopes/checkout/utils/getSelectedMobilePlan'
import { hasCollectionPointAddressCompleted } from '~/scopes/checkout/utils/hasCollectionPointAddressCompleted'
import { hasCollectionPointOptionSelected } from '~/scopes/checkout/utils/hasCollectionPointOptionSelected'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import translations from './AddressConfirmation.translations'
import type { FormValues } from './AddressConfirmation.types'
import Addresses from './components/Addresses/Addresses.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  lastCollectionPoint: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  swapOffer: BuybackOffer | null
}>()

const emit = defineEmits<{
  (e: 'update', callback: () => void): void
}>()

const form = ref<HTMLFormElement | null>(null)

const isSubmitting = ref(false)
const hasCollectionPointError = ref(false)

const i18n = useI18n()
const tracking = useTracking()
const route = useRoute()
const logger = useLogger()
const $httpFetch = useHttpFetch()
const { openErrorToast } = useTheToast()

const addressStore = useAddressStore()
const userInformationStore = useUserInformationStore()
const swapStore = useSwapStore()
const { saveBouyguesAddress } = useSaveBouyguesAddress()
const { resetBuybackOffer } = useBuybackOffer()

const { handleUnauthorizedUser } = useHandleUnauthorizedUser()

onMounted(async () => {
  if (!isEmpty(route.query.missingData)) {
    openErrorToast({
      content: i18n(translations.missingData),
    })

    // Trigger the form submit to display the missing fields error
    form.value?.submit()
  }
})

const validators = makeValidate<FormValues>({
  ...insertIf(userInformationStore.isFormRequired, {
    birthdate: InputAddressBirthdateValidators(i18n),
    nationalId: InputAddressNationalIdValidators(i18n),
  }),
})

const initialValues = computed((): FormValues => {
  const nationalId = addressStore.billing?.customerIdNumber

  return {
    country: addressStore.billing?.country || '',
    birthdate: userInformationStore.birthdate || '',
    // isEmpty from lodash does not tell Typescript that the variable can no longer be null
    // More info: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/45521
    ...insertIf(!isEmpty(nationalId), { nationalId } as { nationalId: string }),
  }
})

const withNationalId = computed(() => {
  return isNationalIdRequiredForCountry(
    addressStore.billing.country as NationalIdCountries,
  )
})

const userInformationWithNationalId = computed(() => {
  return isNationalIdShownForBilling(addressStore.billing.country as Country)
})

const isSubmitButtonDisabled = (hasError?: boolean) =>
  userInformationStore.isFormRequired &&
  (isSubmitting.value || props.loading || hasError)

const handleUserInformationSubmit = async (values: FormValues) => {
  tracking.trackFormSubmit({
    name: 'Address submit',
    address: { ...values },
    type: CHECKOUT_LOG_TYPES.ADDRESS_SUBMIT_CONFIRMATION,
  })

  isSubmitting.value = true

  try {
    const address: Bill = {
      ...addressStore.billing,
      ...values,
      countryDialInCode: addressStore.shipping.countryDialInCode,
      phone: addressStore.shipping.phone,
      ...insertIf(
        userInformationWithNationalId.value && !isEmpty(values.nationalId),
        {
          customerIdNumber: values.nationalId as string,
        },
      ),
      ...insertIf(withNationalId.value && !isEmpty(values.nationalId), {
        customerIdNumber: values.nationalId as string,
      }),
    }

    const formType = userInformationStore.isFormRequired
      ? userInformationStore.formType
      : undefined

    if (hasSelectedMobilePlan(props.items)) {
      await saveBouyguesAddress(address, 'BILLING')
    }

    await addressStore.saveAddress({
      address,
      formType,
      isShipping: false,
      isBilling: true,
    })

    return true
  } catch (error) {
    const err = error as Record<string, unknown>

    if (err?.status === 400) {
      logger.error('[Checkout] Error while shubmitting user information', {
        owners: ['bot-squad-checkout-front'],
      })
    } else {
      await handleUnauthorizedUser(err)
    }

    return false
  } finally {
    isSubmitting.value = false
  }
}
const handleBuybackAddressCreation = async () => {
  isSubmitting.value = true
  const values = addressStore.shipping

  try {
    await addressStore.createBuybackAddress({
      ...values,
    })

    return true
  } catch (err) {
    const error = err as Error
    logger.error('[BUYBACK][SWAP] Error while submitting customer address', {
      error,
      owners: ['bot-squad-checkout-front'],
    })

    return false
  } finally {
    isSubmitting.value = false
  }
}

const handleSubmit = async (values: FormValues) => {
  tracking.trackClick({
    zone: 'address',
    name: 'go_payment',
  })

  // Check if collection point option has a selected collection point
  if (
    hasCollectionPointOptionSelected(props.items) &&
    !hasCollectionPointAddressCompleted(props.lastCollectionPoint)
  ) {
    hasCollectionPointError.value = true

    const listing = props.items.find((item) =>
      item.options.some((option) =>
        option.choices.some(
          (choice) => choice.isCollectionPoint && choice.selected,
        ),
      ),
    )

    const shipping = (listing?.options[0].choices || []).find(
      (choice) => choice.isCollectionPoint,
    )

    const element = document.getElementById(
      `cart-shipping-option-${listing?.listingId}-${shipping?.shippingId}`,
    )
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })

    return
  }

  if (userInformationStore.isFormRequired) {
    const isSuccess = await handleUserInformationSubmit(values)

    if (!isSuccess) {
      openErrorToast({
        title: i18n(translations.submitSwapInfoTitle),
        content: i18n(translations.submitSwapInfoError),
      })

      return
    }
  }

  if (swapStore.hasOffer) {
    const isSuccess = await handleBuybackAddressCreation()

    // Remove the swap offer and proceed to payment -> CK-5631
    if (!isSuccess) {
      await $httpFetch('/bm/cart/swap', { method: 'DELETE' })
      resetBuybackOffer()
    }
  }

  emit('update', async () => {
    isSubmitting.value = true
    await navigateToPaymentPage({ name: CHECKOUT.PAYMENT })
  })
}

const onUpdatingDelivery = () => {
  isSubmitting.value = true
}

const onUpdatedDelivery = async () => {
  emit('update', () => {
    hasCollectionPointError.value = false
    isSubmitting.value = false
  })
}
</script>
